import { createReducer, on } from '@ngrx/store';
import { Bundle } from 'fhir/r4';

import { RequestStatus } from '@patient-ui/shared/enums';
import {
  PortalUser,
  Report,
  ReportHeader,
  ReportHeaderSummary,
  ResultBundle,
  Trends,
} from '@patient-ui/shared/models';

import { ResultActions } from './results.actions';
import * as AuthActions from '../auth/auth.actions';

export const resultsFeatureKey = 'results';

export interface ResultsState {
  results: ReportHeaderSummary;
  resultHeaders: ReportHeader[] | undefined;
  loadResultsPayloadRequestStatus: RequestStatus;
  loadResultHeadersStatus: RequestStatus;
  resultDetail?: Report;
  getResultDetailStatus?: RequestStatus;
  getResultPdfRequestStatus: RequestStatus;
  resultPdf: Blob | undefined;
  resultHistorySelectedUser?: PortalUser;
  error?: string;
  bundle: Bundle | undefined;
  resultBundleList: ResultBundle[];
  testTrends: Trends[];
}

export const ResultsInitialState: ResultsState = {
  results: {
    new: [],
    recent: [],
    upcoming: [],
    updated: [],
    recently_viewed: [],
    totalNew: 0,
    totalUpcoming: 0,
    totalUpdated: 0,
    totalRecentlyViewed: 0,
    totalAll: 0,
    hasResult: false,
    dashboardResults: [],
    dashboardResultsSummary: (results: ReportHeader[]) => results,
    isOnDemand: (accountName: string) => (accountName ? false : true),
  },
  resultHeaders: [],
  loadResultsPayloadRequestStatus: RequestStatus.NotSent,
  loadResultHeadersStatus: RequestStatus.NotSent,
  getResultDetailStatus: RequestStatus.NotSent,
  getResultPdfRequestStatus: RequestStatus.NotSent,
  resultPdf: undefined,
  resultBundleList: [],
  bundle: undefined,
  testTrends: [],
};

export const resultsReducer = createReducer(
  ResultsInitialState,
  on(ResultActions.loadResultsSuccess, (state, { results }) => ({
    ...state,
    results,
    loadResultsPayloadRequestStatus: RequestStatus.Success,
  })),
  on(ResultActions.loadResultsFailure, (state, action) => ({
    ...state,
    loadResultsPayloadRequestStatus: RequestStatus.Failure,
    error: action.error,
  })),
  on(ResultActions.loadResultHeaders, (state) => ({
    ...state,
    resultHeaders: undefined,
    loadResultHeadersStatus: RequestStatus.Pending,
  })),
  on(ResultActions.loadResultHeadersSuccess, (state, results) => ({
    ...state,
    resultHeaders: results.resultHeaders,
    loadResultHeadersStatus: RequestStatus.Success,
  })),
  on(ResultActions.loadResultHeadersFailure, (state, action) => ({
    ...state,
    resultHeaders: [],
    loadResultHeadersStatus: RequestStatus.Failure,
    error: action.error,
  })),
  on(ResultActions.getResultDetail, (state) => ({
    ...state,
    getResultDetailStatus: RequestStatus.Pending,
    resultDetail: undefined,
  })),
  on(ResultActions.getResultDetailSuccess, (state, reportDetail) => ({
    ...state,
    resultDetail: reportDetail.report,
    getResultDetailStatus: RequestStatus.Success,
  })),
  on(ResultActions.getResultDetailFailure, (state, action) => ({
    ...state,
    getResultDetailStatus: RequestStatus.Failure,
    error: action.error,
  })),
  on(ResultActions.getResultPdfSuccess, (state, action) => ({
    ...state,
    resultPdf: action.payload,
    getResultPdfRequestStatus: RequestStatus.Success,
  })),
  on(ResultActions.getResultPdfFailure, (state, action) => ({
    ...state,
    resultPdf: undefined,
    getResultPdfRequestStatus: RequestStatus.Failure,
    error: action.error,
  })),
  on(ResultActions.clearResultPdf, (state) => ({
    ...state,
    resultPdf: undefined,
    getResultPdfRequestStatus: RequestStatus.NotSent,
  })),
  on(ResultActions.setResultHistorySelectedUser, (state, { selectedUser }) => ({
    ...state,
    resultHistorySelectedUser: selectedUser,
  })),
  on(ResultActions.clearResults, (state) => ({
    ...state,
    results: ResultsInitialState.results,
    resultHeaders: ResultsInitialState.resultHeaders,
    loadResultHeadersStatus: ResultsInitialState.loadResultHeadersStatus,
  })),
  on(ResultActions.getResultTrendsSuccess, (state, action) => ({
    ...state,
    resultBundleList: action.resultBundleList ?? [],
  })),
  on(ResultActions.getResultTrendsFailure, (state, action) => ({
    ...state,
    bundle: undefined,
    error: action.error,
  })),
  on(ResultActions.setBundle, (state, action) => ({
    ...state,
    bundle: action.bundle,
  })),
  on(AuthActions.logOutSuccess, () => ResultsInitialState)
);
